import { Button, Dialog, Popover, type DialogProps } from '@fiveox/ui'
import { Info } from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'

/**
 * What the Admin screens share. Each is a read view with its edits in dialogs:
 * a dialog stages a copy, Save applies it, and a refusal from the service shows
 * inside the dialog that caused it.
 */

/** The staged copy of what a dialog edits. Reopening starts from what is saved. */
export function useDraft<T>(open: boolean, from: () => T): [T, (next: T) => void] {
  const [draft, setDraft] = useState<T>(from)
  useEffect(() => {
    if (open) setDraft(from())
    // `from` reads the saved value at the moment of opening.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])
  return [draft, setDraft]
}

/**
 * The ⓘ beside a screen's one-line intro (W26 FR-63, W27 FR-64; plan D79, D81):
 * what the line leaves out, in the kit's `Popover` — a click opens it (a phone
 * has no hover), Escape closes it and focus goes back. On a phone the panel is
 * pinned between the gutters, wherever the ⓘ fell in the wrapped line.
 */
export function AboutPopover({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Popover
      label={label}
      className="max-sm:fixed max-sm:inset-x-4 max-sm:top-24 max-sm:mt-0 max-sm:w-auto"
      trigger={({ open: _open, ...triggerProps }) => (
        <Button {...triggerProps} variant="ghost" size="sm" className="h-6 w-6 px-0" aria-label={label}>
          <Info size={15} aria-hidden />
        </Button>
      )}
    >
      <div className="text-muted-foreground space-y-2">{children}</div>
    </Popover>
  )
}

/** A dialog that is one form: Enter submits it, and the service's refusal
 *  shows above the fields (`Dialog`'s `error`). */
export function FormDialog({
  title,
  open,
  error,
  width,
  onClose,
  onSubmit,
  children,
}: {
  title: string
  open: boolean
  error: string
  width?: DialogProps['width']
  onClose: () => void
  onSubmit: () => void
  children: ReactNode
}) {
  return (
    <Dialog open={open} onClose={onClose} title={title} error={error} width={width}>
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault()
          onSubmit()
        }}
      >
        {children}
      </form>
    </Dialog>
  )
}

/** Cancel and submit, with a note on the left — how many changes are staged,
 *  or what submitting will do. */
export function DialogFooter({
  busy,
  onClose,
  note,
  submitLabel = 'Save',
  busyLabel = 'Saving…',
  disabled,
  tone = 'default',
}: {
  busy: boolean
  onClose: () => void
  note?: ReactNode
  submitLabel?: string
  busyLabel?: string
  disabled?: boolean
  /** `danger` for a submit that archives something. */
  tone?: 'default' | 'danger'
}) {
  return (
    <div className="mt-6 flex flex-wrap items-center justify-between gap-2">
      <span className="text-muted-foreground text-xs">{note}</span>
      <div className="ml-auto flex gap-2">
        <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant={tone === 'danger' ? 'destructive' : 'primary'}
          loading={busy}
          loadingText={busyLabel}
          disabled={disabled}
        >
          {submitLabel}
        </Button>
      </div>
    </div>
  )
}

/** "2 changes" / "No changes yet" — the footer's note in an edit dialog. */
export const changesNote = (n: number) => (n ? `${n} change${n === 1 ? '' : 's'}` : 'No changes yet')

/** "1 document", "3 documents". */
export const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

/** "Purchase, Stores and Finance" / "Purchase or Stores". */
export function listOf(items: string[], joiner: 'and' | 'or' = 'and'): string {
  return new Intl.ListFormat('en-GB', { style: 'long', type: joiner === 'and' ? 'conjunction' : 'disjunction' }).format(items)
}

/** What a sentence calls someone: their first name, or the whole of it when
 *  it is an email address. */
export const firstName = (name: string) => (name.includes('@') ? name : (name.trim().split(/\s+/)[0] ?? name))

/** The same check the service makes. */
export const isEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i.test(v)
